import cn from 'client/utils/cn';

import React, { Component } from 'react';

// @ts-ignore
import SwipeListener from 'swipe-listener';

import './index.css';

const b = cn('yablogs-post');

interface Props {
    rawHtml: string;
}

const MAX_IMAGE_HEIGHT = 600;

export default class YablogsPost extends Component<Props> {
    constructor(props: Props) {
        super(props);

        this.changeArrowsVisibility = this.changeArrowsVisibility.bind(this);
        this.onPrevArrowClick = this.onPrevArrowClick.bind(this);
        this.onNextArrowClick = this.onNextArrowClick.bind(this);
        this.onBulletClick = this.onBulletClick.bind(this);
    }

    componentDidMount() {
        document.querySelectorAll('.yablogs-gallery').forEach(this.initGallery);
    }

    initGallery = (gallery: HTMLElement) => {
        const galleryNodes = Array.from(gallery.children) as HTMLElement[];

        const slides = galleryNodes.filter(node => node.classList.contains('yablogs-gallery__slide'));
        const arrowLeft = galleryNodes.find(node => node.classList.contains('yablogs-gallery__arrow-left'));
        const arrowRight = galleryNodes.find(node => node.classList.contains('yablogs-gallery__arrow-right'));

        if (!arrowLeft || !arrowRight || slides.length === 0) {
            return;
        }

        const bulletsContainer = this.createBulletContainer(gallery);

        slides.forEach((el, index) => {
            el.classList.add(index === 0 ? '_active' : '_next');
            el.setAttribute('data-index', String(index));
            this.createBullet(bulletsContainer, index);
        });

        this.initGalleryEvents(gallery, bulletsContainer, slides, arrowLeft, arrowRight);
        this.changeArrowsVisibility(slides, arrowLeft, arrowRight);

        gallery.style.height = `${this.getMaxImageHeight(slides)}px`;
        gallery.classList.add('_visible');
    }

    initGalleryEvents = (
        gallery: HTMLElement,
        bulletsContainer: HTMLElement,
        slides: HTMLElement[],
        arrowLeft: HTMLElement,
        arrowRight: HTMLElement
        // eslint-disable-next-line max-params
    ) => {
        const bullets = Array.from(bulletsContainer.children) as HTMLElement[];

        arrowLeft.addEventListener('click', () => this.onPrevArrowClick(slides, arrowLeft, arrowRight, bullets));
        arrowRight.addEventListener('click', () => this.onNextArrowClick(slides, arrowLeft, arrowRight, bullets));
        bullets.forEach(bullet => {
            bullet.addEventListener('click', (e: MouseEvent) => this.onBulletClick(e, bullets, slides));
        });

        // eslint-disable-next-line no-new
        new SwipeListener(gallery);

        gallery.addEventListener('swipe', e => {
            // @ts-ignore
            const { directions } = e.detail;
            const left = directions.left || (directions.left && directions.top);
            const right = directions.right || (directions.right && directions.top);

            if (left) {
                this.onNextArrowClick(slides, arrowLeft, arrowRight, bullets);
            }
            if (right) {
                this.onPrevArrowClick(slides, arrowLeft, arrowRight, bullets);
            }
        });
    }

    render() {
        return <div className={b()} dangerouslySetInnerHTML={{ __html: this.props.rawHtml }} />;
    }

    private getMaxImageHeight(slides: HTMLElement[]): number {
        let maxHeight = MAX_IMAGE_HEIGHT;

        slides.forEach(slide => {
            const [image] = Array.from(slide.children) as HTMLImageElement[];

            // В некоторых слайдах нет изображений
            if (image.nodeName.toLowerCase() !== 'img') {
                return;
            }

            maxHeight = Math.max(maxHeight, image.height);

        });

        return maxHeight;
    }

    private createBulletContainer(gallery: HTMLElement): HTMLElement {
        const bulletsContainer = document.createElement('div');

        bulletsContainer.className = 'yablogs-gallery__bullets';
        gallery.append(bulletsContainer);

        return bulletsContainer;
    }

    private createBullet(bulletsContainer: HTMLElement, index: number): void {
        const bulletNode = document.createElement('div');

        bulletNode.className = 'yablogs-gallery__bullet';
        bulletNode.setAttribute('data-index', String(index));
        if (index === 0) {
            bulletNode.classList.add('_selected');
        }

        bulletsContainer.append(bulletNode);
    }

    private getSlideByClassName(slides: HTMLElement[], className: string, params?: {fromEnd: boolean}): HTMLElement | undefined {
        const slidesByClassName = slides.filter(slide => slide.classList.contains(className));

        return params?.fromEnd ? slidesByClassName[slidesByClassName.length - 1] : slidesByClassName[0];
    }

    // eslint-disable-next-line max-params
    private onPrevArrowClick(slides: HTMLElement[], arrowLeft: HTMLElement, arrowRight: HTMLElement, bullets: HTMLElement[]): void {
        const active = this.getSlideByClassName(slides, '_active');
        // Берем последний из предыдущих
        const prev = this.getSlideByClassName(slides, '_prev', { fromEnd: true });
        const bulletIndex = prev?.getAttribute('data-index');

        if (prev && active) {
            active.classList.remove('_active');
            active.classList.add('_next');

            prev.classList.remove('_prev');
            prev.classList.add('_active');
        }

        this.changeArrowsVisibility(slides, arrowLeft, arrowRight);
        this.selectBullet(bullets, bulletIndex);
    }

    // eslint-disable-next-line max-params
    private onNextArrowClick(slides: HTMLElement[], arrowLeft: HTMLElement, arrowRight: HTMLElement, bullets: HTMLElement[]): void {
        const active = this.getSlideByClassName(slides, '_active');
        const next = this.getSlideByClassName(slides, '_next');
        const bulletIndex = next?.getAttribute('data-index');

        if (next && active) {
            active.classList.remove('_active');
            active.classList.add('_prev');

            next.classList.remove('_next');
            next.classList.add('_active');
        }

        this.changeArrowsVisibility(slides, arrowLeft, arrowRight);
        this.selectBullet(bullets, bulletIndex);
    }

    private onBulletClick(e: MouseEvent, bullets: HTMLElement[], slides: HTMLElement[]): void {
        const currentBullet = bullets.find(el => el.classList.contains('_selected'));

        if (e.target === currentBullet) {
            return;
        }

        const nextSlideIndex = Number((e.target as HTMLElement).getAttribute('data-index'));

        slides.forEach((slide, index) => {
            slide.classList.remove('_active', '_next', '_prev');

            let className;

            if (index < nextSlideIndex) {
                className = '_prev';
            } else if (index > nextSlideIndex) {
                className = '_next';
            } else {
                className = '_active';
            }

            slide.classList.add(className);
        });

        this.selectBullet(bullets, nextSlideIndex);
    }

    private changeArrowsVisibility(slides: HTMLElement[], arrowLeft: HTMLElement, arrowRight: HTMLElement): void {
        const prev = this.getSlideByClassName(slides, '_prev');
        const next = this.getSlideByClassName(slides, '_next');

        if (prev) {
            arrowLeft.classList.remove('_disable');
        } else {
            arrowLeft.classList.add('_disable');
        }

        if (next) {
            arrowRight.classList.remove('_disable');
        } else {
            arrowRight.classList.add('_disable');
        }
    }

    private selectBullet(bullets: HTMLElement[], bulletIndex: number | string | null | undefined): void {
        if (!bulletIndex) {
            return;
        }

        const currentBullet = bullets.find(el => el.classList.contains('_selected'));
        const nextBullet = bullets.find(el => el.getAttribute('data-index') === String(bulletIndex));

        currentBullet?.classList.remove('_selected');
        nextBullet?.classList.add('_selected');
    }
}
